import { type ReactNode, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, SectionLabel } from './Layout';

/**
 * Collapsible — a Card with a toggle header, for secondary sections that
 * don't need to be open by default (e.g. Documents, Timeline, Parsing
 * Results). Accessible: the toggle is a button with aria-expanded and
 * aria-controls; the body is hidden from the a11y tree when collapsed.
 *
 * `actions` render to the right of the header as siblings of the toggle
 * (not nested in it), so header buttons stay independently clickable.
 */
export function Collapsible({
  title,
  count,
  icon,
  actions,
  defaultOpen = false,
  children,
}: {
  title: string;
  /** Optional count shown next to the title, e.g. number of documents. */
  count?: number;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional header controls, rendered to the right (not part of the toggle). */
  actions?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <Card padding={false}>
      <div className="flex items-center gap-2 pr-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex-1 min-w-0 flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-900/[0.03] rounded-glass transition-colors"
        >
          <ChevronDown
            className={cn(
              'w-4 h-4 text-text-muted shrink-0 transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
          {icon}
          <SectionLabel>{title}</SectionLabel>
          {count !== undefined && (
            <span className="text-caption text-text-tertiary tabular-nums">{count}</span>
          )}
        </button>
        {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
      </div>
      <div id={bodyId} hidden={!open}>
        {open && <div className="border-t border-glass-hairline">{children}</div>}
      </div>
    </Card>
  );
}
